import { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase';
import { collection, query, where, getDocs, addDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';

const useSavedCars = (userId) => {
    const [savedCarIds, setSavedCarIds] = useState(new Set());
    const [loading, setLoading] = useState(true);

    // Fetch saved cars for this user
    useEffect(() => {
        if (!userId) {
            setSavedCarIds(new Set());
            setLoading(false);
            return;
        }

        const fetchSavedCars = async () => {
            try {
                const savedCarsRef = collection(db, 'savedCars');
                const q = query(savedCarsRef, where('userId', '==', userId));
                const snapshot = await getDocs(q);
                const ids = new Set(snapshot.docs.map(doc => doc.data().modelId));
                setSavedCarIds(ids);
            } catch (error) {
                console.error('Error fetching saved cars:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchSavedCars();
    }, [userId]);

    const toggleSavedCar = useCallback(async (modelId) => {
        if (!userId) return;

        try {
            const savedCarsRef = collection(db, 'savedCars');

            if (savedCarIds.has(modelId)) {
                // Remove: find the doc and delete it
                const q = query(savedCarsRef, where('userId', '==', userId), where('modelId', '==', modelId));
                const snapshot = await getDocs(q);
                for (const docSnap of snapshot.docs) {
                    await deleteDoc(doc(db, 'savedCars', docSnap.id));
                }
                setSavedCarIds(prev => {
                    const next = new Set(prev);
                    next.delete(modelId);
                    return next;
                });
            } else {
                // Add
                await addDoc(savedCarsRef, {
                    userId,
                    modelId,
                    savedAt: serverTimestamp()
                });
                setSavedCarIds(prev => new Set(prev).add(modelId));
            }
        } catch (error) {
            console.error('Error toggling saved car:', error);
        }
    }, [userId, savedCarIds]);

    const isSaved = useCallback((modelId) => {
        return savedCarIds.has(modelId);
    }, [savedCarIds]);

    return { savedCarIds, isSaved, toggleSavedCar, loading };
};

export default useSavedCars;
